import { useParams } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";

import { leadershipData } from "../data/leadershipData";
import AnimatedPage from "../component/LeadershipComponents/AnimatedPage";
import HorizontalScroller from "../component/LeadershipComponents/HorizontalScroller";
import ProfileCard from "../component/LeadershipComponents/ProfileCard";
import Navbar from "../component/Navbar";
import Footer from "../component/Footer";

const PersonProfile = () => {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const allPeople = [
    leadershipData.chairman,
    ...leadershipData.officeBearers,
  ];

  const person = allPeople.find((p) => p.id === id);

  if (!person) {
    return (
      <>
        <Navbar />
        <div className="text-center py-24 text-lg font-medium">
          Person not found
        </div>
        <Footer />
      </>
    );
  }

  let suggestions = allPeople.filter((p) => p.id !== id);
  suggestions = suggestions.filter(
    (p, idx, arr) => arr.findIndex(q => q.name === p.name) === idx
  );

  return (
    <>
      <Navbar />

      <AnimatedPage>
        <div className="bg-gradient-to-b from-white to-gray-50 min-h-screen">
          <div className="max-w-5xl mx-auto px-5 py-12">

            {/* ===== Top Section ===== */}
            <motion.div
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-8"
            >
              {/* Image */}
              <div className="w-52 h-52 md:w-60 md:h-60 rounded-full overflow-hidden border-4 border-blue-100 shadow-xl">
                <img
                  src={person.image}
                  alt={person.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Info */}
              <div className="text-center md:text-left max-w-lg">
                <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-2">
                  {person.name}
                </h1>

                <p className="text-lg md:text-xl text-blue-600 font-medium mb-3">
                  {person.designation}
                </p>

                {person.shortBio && (
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-3">
                    {person.shortBio}
                  </p>
                )}

                {/* 🔗 Enhanced LinkedIn Button */}
             {person.linkedin && (
  <a
    href={person.linkedin}
    target="_blank"
    rel="noopener noreferrer"
    className="group inline-flex items-center gap-3 px-5 py-2.5 bg-[#0A66C2] text-white text-sm font-medium rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
  >
    {/* Icon Container */}
    <span className="flex items-center justify-center w-7 h-7 bg-white/20 rounded-full group-hover:bg-white/30 transition">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-4 h-4"
      >
        <path d="M20.45 20.45h-3.55v-5.4c0-1.29-.03-2.95-1.8-2.95-1.8 0-2.07 1.4-2.07 2.85v5.5H9.48V9h3.4v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.26 2.37 4.26 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.11 20.45H3.57V9h3.54v11.45z" />
      </svg>
    </span>

    {/* Text */}
    <span className="tracking-wide">
      View LinkedIn
    </span>
  </a>
)}
              </div>
            </motion.div>

            {/* ===== Biography Section ===== */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-md p-8 mb-12"
            >
              <h2 className="text-2xl font-semibold text-blue-800 mb-5">
                Biography
              </h2>

              {person.fullProfile ? (
                <div className="space-y-5 text-gray-700 leading-relaxed text-base">
                  <p>{person.fullProfile.bio}</p>

                  {person.fullProfile.achievements && (
                    <>
                      <h3 className="text-xl font-semibold text-blue-700 mt-6">
                        Achievements
                      </h3>
                      <ul className="list-disc pl-5 space-y-1">
                        {person.fullProfile.achievements.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {person.fullProfile.experience && (
                    <>
                      <h3 className="text-xl font-semibold text-blue-700 mt-6">
                        Experience
                      </h3>
                      <p>{person.fullProfile.experience}</p>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-gray-600">
                  Detailed profile coming soon.
                </p>
              )}
            </motion.div>

            {/* ===== Suggestions ===== */}
            {suggestions.length > 0 && (
              <section>
                <h3 className="text-2xl font-bold text-blue-900 mb-6">
                  Other Office Bearers
                </h3>

                <HorizontalScroller>
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.id}
                      className="min-w-[260px] max-w-[260px]"
                    >
                      <ProfileCard person={suggestion} basePath="leadership" />
                    </div>
                  ))}
                </HorizontalScroller>
              </section>
            )}

          </div>
        </div>
      </AnimatedPage>

      <Footer />
    </>
  );
};

export default PersonProfile;